<script>
    import { keyScope } from '../scripts/keyboard';
    import ActionButton from './components/ActionButton.svelte';
    import CopyButton from '../CopyButton.svelte';
    import PlaygroundRating from '../PlaygroundRating.svelte';
    import ApiClient from '../scripts/apiclient';
    import { createActionRunner } from '../scripts/ActionRunner';
    import PlaygroundHelper from '../scripts/playgroundhelper';

    const { busy, run } = createActionRunner();

    /**
     * The group of photos to approve. All photos belong to the same playground and
     * were posted by the same user.
     */
    export let group;

    /**
     * The photos of the group.
     */
    $: photos = group.photos;

    /**
     * The playground of the photos.
     */
    $: playground = group.playground;

    /**
     * Function to close the surrounding popup.
     */
    export let closePopup;

    /**
     * Function to split the group into separate items, to handle the photos one by one.
     */
    export let splitGroup;

    const approveAll = run(() => ApiClient.approvePhotos(photos).then(close));

    const rejectAll = run(() => ApiClient.rejectPhotos(photos).then(close));

    function split() {
        if ($busy || !splitGroup) {
            return;
        }

        splitGroup();
    }

    // This wrapper prevents the mouse event to be sent to close()
    function cancel() {
        close();
    }

    // Closing is possible while an action is running, so that a hanging action can be
    // abandoned and retried. The flag makes sure that an action finishing after that
    // doesn't close the popup a second time, closing whatever has been opened since.
    let closed = false;

    function close() {
        if (closed || !closePopup) {
            return;
        }

        closed = true;
        closePopup();
    }

    keyScope({ Enter: approveAll, Escape: cancel, d: split }, { modal: true });
</script>

<h3>{PlaygroundHelper.presentPlayground(playground, false)}</h3>
<span class="subtitle"><i class="fas fa-map-marker" /> <span class="id-text">{playground._id}</span><CopyButton text={playground._id} title="Kopiera lekplatsens id" /></span>
<span class="subtitle"><i class="fas fa-image" /> {photos.length} nya foton</span>
<PlaygroundRating {playground} />

<div class="photos">
    {#each photos as photo}
        <img
            src={ApiClient.getThumbnailUrl(photo._id, 150)}
            width="150"
            height="150"
            alt="Foto av lekplatsen"
            title={photo._id}
        />
    {/each}
</div>

<div class="button-wrapper-wrapper">
    <div class="button-wrapper">
        <button class="btn btn-sm" on:click={cancel}>Avbryt</button>
        <ActionButton {busy} action={split} variant="btn-secondary" title="Hantera fotona ett i taget"><u>D</u>ela upp</ActionButton>
        <ActionButton {busy} action={rejectAll} variant="btn-warning" title="Avslå alla fotona">Avslå</ActionButton>
        <ActionButton {busy} action={approveAll} name="approve" title="Godkänn alla fotona">Godkänn</ActionButton>
    </div>
</div>

<style>
    h3 {
        margin-bottom: 0;
    }

    .subtitle {
        color: #444;
        padding: 0;
        margin: 0;
    }

    /* A grid rather than a run of thumbnails set side by side: laid out that way the last
       row trailed off wherever it happened to end, and whatever was left over at the right
       stayed empty. The columns divide the width between them however much there is, so
       every row reaches both edges. */
    .photos {
        display: grid;
        grid-template-columns: repeat(auto-fill, minmax(120px, 1fr));
        gap: 2px;
        padding: 2px;
        margin-top: 5px;
        max-height: 470px;
        overflow-y: auto;
        background: #eee;
    }

    .photos img {
        width: 100%;
        height: auto;
        /* The thumbnails are asked for square, and this keeps a row even if one comes back
           otherwise */
        aspect-ratio: 1 / 1;
        object-fit: cover;
    }

    /* Smaller ones on a phone: three to a row rather than one, which shows the group at a
       glance instead of a photo at a time, and leaves room for the buttons below. */
    @media (max-width: 575.98px) {
        .photos {
            grid-template-columns: repeat(auto-fill, minmax(90px, 1fr));
            max-height: 50vh;
            max-height: 50dvh;
        }
    }

    .button-wrapper-wrapper {
        margin-top: 15px;
    }

    /* A row that wraps rather than four buttons floated with a margin each, which is what
       used to leave the last of them hanging on a short second line. With the labels as
       short as they are the four fit on one line at any width worth having. */
    .button-wrapper {
        display: flex;
        flex-wrap: wrap;
        justify-content: flex-end;
        align-items: center;
        gap: 5px;
    }

    /* Global, since the action buttons live in their own component */
    .button-wrapper :global(.btn) {
        margin: 0;
    }
</style>
